
























































































































































































































import {
  defineComponent,
  reactive,
  computed,
  ref,
  watch,
  Ref,
  onMounted,
} from '@vue/composition-api';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import PrettyCheck from 'pretty-checkbox-vue/check';

import { useRouter, useFormat } from '@ideavivat/vivat-2';

import useAuth from '@/useAuth';
import usePayment from '@/usePayment';
import type { ID } from '@/types';

import getDelivery from '@/gql/delivery/getDelivery.gql';
import createDelivery from '@/gql/delivery/createDelivery.gql';
import updateDelivery from '@/gql/delivery/updateDelivery.gql';
import findAreaCode from '@/gql/delivery/findAreaCode.gql';

import ItemOrderForFlatRate from '@/components/Delivery/ItemOrderForFlatRate.vue';
import VehicleSelectorInput from '@/components/DeliveryNote/VehicleSelectorInput.vue';
import GroupAddressEditor from '@/components/LocationAddress/GroupAddressEditor.vue';
import StickyFooterActionBar from '@/components/StickyFooterActionBar.vue';
import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonTimePicker from '@/components/CommonTimePicker.vue';
import CommonAreaCodeSelector from '@/components/Setting/CommonAreaCodeSelector.vue';
import CommonProductSelector from '@/components/Product/CommonProductSelector.vue';
import { DEFAULT_DELIVERY } from '@/useDelivery';
import { computerTime } from '@/utils';
import { sumProductPrice } from '@/components/Delivery/useCreateDelivery';

const CommonFormWithDeliveryFlatRate = defineComponent({
  components: {
    ItemOrderForFlatRate,
    GroupAddressEditor,
    StickyFooterActionBar,
    CommonModalWithPreset,
    CommonTimePicker,
    CommonAreaCodeSelector,
    CommonProductSelector,
    VehicleSelectorInput,
    PrettyCheck,
  },
  props: {
    edit: {
      default() { return false; },
    },
  },
  setup(props: any) {
    const { route } = useRouter();
    const { me } = useAuth();
    const { th: paymentMethodTH, en: paymentMethodEN } = usePayment();

    const toDecimalFormat = (string: any) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const NOW = new Date();
    const client = reactive({
      ...DEFAULT_DELIVERY,
      productDetails: {},
    });

    const enableTargetDate = ref(false);

    const addItem = () => {
      client.packages.push({
        id: -1,
        name: '',
        code: '',
        width: '',
        length: '',
        height: '',
        count: 0,
        weight: '',
        unit: '',
        deliveryFee: 0,
        price: 0,
        itemGroup: '',
        deliveryFeePerUnit: 0,
        productId: -1,
      });
      client.productDetails = '';
    };
    const deletedPackages: Ref<ID[]> = ref([]);
    const createdPackages = computed(() => client.packages.filter((item: any) => (
      item.id === -1
    )).map((item: any) => ({
      ...item,
      id: undefined,
      width: parseFloat(item.width),
      length: parseFloat(item.length),
      height: parseFloat(item.height),
      volume: parseFloat(item.width) * parseFloat(item.length) * parseFloat(item.height) || 0,
      weight: parseFloat(item.weight),
      totalWeight: parseFloat(item.weight) * parseFloat(item.count),
      count: parseFloat(item.count),
      deliveryFee: parseFloat(item.deliveryFee),
      price: parseFloat(item.price),
      deliveryFeeRate: undefined,
      deliveryFeePerUnit: undefined,
      productId: undefined,
      itemGroup: undefined,
    })));
    const updatedPackages = computed(() => client.packages.filter((item: any) => (
      item.id !== -1
    )).map((item: any) => ({
      ...item,
      width: parseFloat(item.width),
      length: parseFloat(item.length),
      height: parseFloat(item.height),
      volume: parseFloat(item.width) * parseFloat(item.length) * parseFloat(item.height) * item.count || item.volume,
      weight: parseFloat(item.weight),
      totalWeight: parseFloat(item.weight) * parseFloat(item.count),
      count: parseFloat(item.count),
      deliveryFee: parseFloat(item.deliveryFee),
      price: parseFloat(item.price),
      deliveryFeeRate: undefined,
      productId: undefined,
      itemGroup: undefined,
      deliveryFeePerUnit: undefined,
    })));
    const pop = (index: number) => {
      if (client.packages[index]) {
        deletedPackages.value.push(client.packages[index].id);
        client.packages.splice(index, 1);
      }
    };

    const computedTotalDeliveryFee = computed(() => {
      if (client.packages.length === 0) return 0;
      const res = client.packages.reduce((a: any, b: any) => parseFloat(a + parseFloat(b.deliveryFee)), 0) - parseFloat(client.deliveryDiscount as any);
      if (res >= 0) return res;
      return 0;
    });

    const computedPackagesInput = computed(() => client.packages.map((item: any) => ({
      ...item,
      id: undefined,
      // eslint-disable-next-line
      product_id: item.id,
      width: parseFloat(item.width) || 0,
      length: parseFloat(item.length) || 0,
      height: parseFloat(item.height) || 0,
      volume: parseFloat(item.width) * parseFloat(item.length) * parseFloat(item.height) * item.count || 0,
      weight: parseFloat(item.weight) || 0,
      totalWeight: parseFloat(item.weight) * parseFloat(item.count),
      count: parseFloat(item.count),
      deliveryFee: parseFloat(item.deliveryFee),
      price: parseFloat(item.price),
      deliveryFeeRate: undefined,
      itemGroup: undefined,
      productId: undefined,
      deliveryFeePerUnit: undefined,
    })));
    const orderInput = computed(() => ({
      orderDate: `${useFormat.date(client.orderDate).computerShortDate()} ${computerTime()}`,
      orderTime: undefined,
      customerCode: client.customerCode,
      customerName: client.customerName,
      contactName: client.contactName,
      orderNumber: client.orderNumber,
      deliveryDiscount: parseFloat(client.deliveryDiscount as any),
      address: {
        ...client.address,
        address: client.address.address,
        province: client.address.province.id,
        district: client.address.district.id,
        subdistrict: client.address.subdistrict.id,
        zipcode: `${client.address.zipcode}`,
      },
      senderName: client.senderName,
      senderCode: client.senderCode,
      senderAddress: {
        senderAddress: client.senderAddress.address !== '' ? client.senderAddress.address : undefined,
        senderProvince: client.senderAddress.province.id !== '' ? client.senderAddress.province.id : undefined,
        senderDistrict: client.senderAddress.district.id !== '' ? client.senderAddress.district.id : undefined,
        senderSubdistrict: client.senderAddress.subdistrict.id !== '' ? client.senderAddress.subdistrict.id : undefined,
        senderZipcode: `${client.senderAddress.zipcode}`,
        senderTelephone: client.senderAddress.telephone !== '' ? client.senderAddress.telephone : undefined,
      },
      // deliveryArea: client.orderZone,
      area: (client.area) ? { connect: client.area } : undefined,
      deliveryFee: computedTotalDeliveryFee.value,
      paymentType: client.paymentType,
      remarks: client.remarks,
      lat: parseFloat(client.lat as any || '0.0'),
      long: parseFloat(client.long as any || '0.0'),
      status: props.edit ? undefined : 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER',
      packages: {
        create: computedPackagesInput.value,
      },
      targetDate: enableTargetDate.value ? client.targetDate : undefined,
      deliveryCode: client.deliveryCode,
      type: 'LEASED',
      reservedVehicle: {
        connect: (client.reservedVehicle as any).id,
      },
    }));

    const refetchQueries = ['getDeliveries', 'getDeliveryNotes', 'getDelivery'];
    const createOrderMutation = useMutation(createDelivery, () => ({
      variables: {
        input: orderInput.value,
      },
      refetchQueries,
    }));

    // FIXME: This already mess up, too many duplicate LOC
    const findAreaCodeMutation = useMutation(findAreaCode);
    const foundedArea: Ref<{ id: ID; code: string }[]> = ref([]);
    const availableArea = computed(() => {
      const list = [];
      const display = [];
      for (let i = 0; i < foundedArea.value.length; i += 1) {
        list.push(foundedArea.value[i].id);
        display.push(foundedArea.value[i].code);
      }
      return {
        list,
        display,
      };
    });
    findAreaCodeMutation.onDone((result: any) => {
      foundedArea.value = result.data.findAreaCode;
    });
    function pendingSearchAreaCode() {
      if (client.address.province.id && client.address.district.id && client.address.subdistrict.id) {
        findAreaCodeMutation.mutate({
          provinceId: client.address.province.id,
          districtId: client.address.district.id,
          subDistrictId: client.address.subdistrict.id,
        });
      }
    }

    const { result: getOldOrderResult, loading } = useQuery(getDelivery, computed(() => ({
      id: route.value.params.id,
    })), () => ({
      enabled: route.value.params.id,
    }));
    const oldOrderResult = useResult(getOldOrderResult, {}, (data) => data.delivery);
    watch(() => oldOrderResult.value, (newVal, oldVal) => {
      if (!loading.value && newVal !== oldVal && props.edit) {
        client.id = oldOrderResult.value.id;
        client.customerName = oldOrderResult.value.customerName;
        client.customerCode = oldOrderResult.value.customerCode;
        client.orderDate = oldOrderResult.value.orderDate;
        client.contactName = oldOrderResult.value.contactName;
        client.orderNumber = oldOrderResult.value.orderNumber;
        client.deliveryDiscount = oldOrderResult.value.deliveryDiscount;
        client.address = {
          address: oldOrderResult.value.address.address || '',
          province: oldOrderResult.value.address.province || { id: null },
          district: oldOrderResult.value.address.district || { id: null },
          subdistrict: oldOrderResult.value.address.subdistrict || { id: null },
          zipcode: oldOrderResult.value.address.zipcode,
          telephone: oldOrderResult.value.address.telephone,
        };
        client.senderName = oldOrderResult.value.senderName;
        client.senderCode = oldOrderResult.value.senderCode;
        client.senderAddress = {
          address: oldOrderResult.value.senderAddress.address,
          province: oldOrderResult.value.senderAddress.province || { id: null },
          district: oldOrderResult.value.senderAddress.district || { id: null },
          subdistrict: oldOrderResult.value.senderAddress.subdistrict || { id: null },
          zipcode: oldOrderResult.value.senderAddress.zipcode !== 'null' ? oldOrderResult.value.senderAddress.zipcode : '',
          telephone: oldOrderResult.value.senderAddress.telephone !== '' ? oldOrderResult.value.senderAddress.telephone : '',
        };
        client.area = oldOrderResult.value.area?.id;
        client.deliveryFee = oldOrderResult.value.deliveryFee;
        client.paymentType = oldOrderResult.value.paymentType;
        client.remarks = oldOrderResult.value.remarks;

        client.lat = oldOrderResult.value.lat;
        client.long = oldOrderResult.value.long;
        client.reservedVehicle = oldOrderResult.value.reservedVehicle;
        client.area = oldOrderResult.value.area.id;
        client.deliveryCode = oldOrderResult.value.deliveryCode;
        pendingSearchAreaCode();

        if (oldOrderResult.value.targetDate && oldOrderResult.value.targetDate !== null && oldOrderResult.value.targetDate !== '') {
          client.targetDate = oldOrderResult.value.targetDate;
          enableTargetDate.value = true;
        }
      }
    });

    watch(() => client.productDetails, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        const packageIndex = client.packages.length - 1;

        client.packages[packageIndex].id = client.packages[packageIndex].id ? client.packages[packageIndex].id : -1;
        client.packages[packageIndex].name = (client.productDetails as any).name;
        client.packages[packageIndex].code = (client.productDetails as any).code;
        client.packages[packageIndex].width = (client.productDetails as any).width;
        client.packages[packageIndex].length = (client.productDetails as any).length as any;
        client.packages[packageIndex].height = (client.productDetails as any).height;
        client.packages[packageIndex].count = 0;
        client.packages[packageIndex].weight = (client.productDetails as any).weightPerUnit;
        client.packages[packageIndex].unit = (client.productDetails as any).unit;
        client.packages[packageIndex].deliveryFee = 0;
        client.packages[packageIndex].price = 0;
        client.packages[packageIndex].productId = (client.productDetails as any).id;
        client.packages[packageIndex].itemGroup = (client.productDetails as any).productGroup;
      }
    });
    watch(() => client.area, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        if (route.value.name === 'DeliveryFlatRateEdit') {
          client.packages = (oldOrderResult.value?.packages || []).map((item: any) => ({
            id: item.id,
            name: item.name,
            code: item.code,
            width: item.width,
            length: item.length,
            height: item.height,
            count: item.count || 1,
            weight: item.weight,
            unit: item.unit,
            deliveryFee: item.deliveryFee,
            itemGroup: item.product?.productGroup || undefined,
            price: item.price,
          }));
        }
      }
    });
    const computedTotalPrice = computed(() => {
      const total = client.packages.reduce(sumProductPrice as any, 0);
      return total;
    });

    const editOrderMutation = useMutation(updateDelivery, () => ({
      variables: {
        input: {
          ...orderInput.value,
          type: undefined,
          id: route.value.params.id,
          packages: {
            delete: deletedPackages.value,
            create: createdPackages.value,
            update: updatedPackages.value,
          },
        },
      },
      refetchQueries,
    }));

    const computedMutation = computed(() => (
      props.edit ? editOrderMutation : createOrderMutation
    ));
    const confirmModal = ref(null);
    createOrderMutation.onDone(() => {
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'รายการส่ง สำเร็จ' });
    });
    editOrderMutation.onDone(() => {
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'รายการส่ง สำเร็จ' });
    });
    const errorModal = ref(null);
    createOrderMutation.onError(() => {
      (errorModal.value as any).compose();
    });
    editOrderMutation.onError(() => {
      (errorModal.value as any).compose();
    });

    return {
      paymentMethodTH,
      paymentMethodEN,

      client,
      addItem,
      pop,

      computedMutation,
      confirmModal,
      errorModal,

      deletedPackages,
      createdPackages,
      updatedPackages,

      computedTotalDeliveryFee,
      enableTargetDate,

      computedTotalPrice,

      toDecimalFormat,

      // FIXME: This already mess up, too many duplicate LOC
      findAreaCodeMutation,
      pendingSearchAreaCode,
      availableArea,
    };
  },
});

export default CommonFormWithDeliveryFlatRate;
